import { Badge, Box, Button, HStack, Input, Stack, Text } from '@chakra-ui/react';
import { toasterApi } from '@ui';
import React from 'react';
import { LuArchive, LuCopy, LuPencil, LuStar } from 'react-icons/lu';
import { TrackingApi } from '@/state/api';
import { useAppSelector } from '@/state';
import { TrackingType } from '@/types';

/** Rename, duplicate, archive, and default controls for saved label settings. */
export const SavedSettingsManager: React.FC = () => {
  const permissions = useAppSelector(
    (s) => s.auth.current.user?.permissions ?? [],
  ) as string[];
  const canShared = permissions.includes('manage_shared_labeltemplate');
  const { data: templates, isLoading } = TrackingApi.Api.useGetTemplatesQuery();
  const { data: profiles } = TrackingApi.Api.useGetProfilesQuery();
  const { data: defaults } = TrackingApi.Api.useGetDefaultsQuery();
  const [patchTemplate] = TrackingApi.Api.usePatchTemplateMutation();
  const [templateAction] = TrackingApi.Api.usePostTemplateActionMutation();
  const [patchProfile] = TrackingApi.Api.usePatchProfileMutation();
  const [putDefaults] = TrackingApi.Api.usePutDefaultsMutation();
  const [renaming, setRenaming] = React.useState<{
    id: number;
    name: string;
    kind: 'template' | 'profile';
  } | null>(null);

  const setDefault = (patch: Partial<TrackingType.UserDefaults>) =>
    putDefaults({
      template_id: defaults?.template_id ?? null,
      printer_profile_id: defaults?.printer_profile_id ?? null,
      preferences: defaults?.preferences ?? {},
      ...patch,
    })
      .unwrap()
      .then(() =>
        toasterApi({
          type: 'success',
          status: 200,
          code: 'Success',
          message: 'Default saved',
        }),
      )
      .catch(() => undefined);

  const rename = async () => {
    if (!renaming) return;
    if (renaming.kind === 'template')
      await patchTemplate({ id: renaming.id, name: renaming.name })
        .unwrap()
        .catch(() => undefined);
    else
      await patchProfile({ id: renaming.id, name: renaming.name })
        .unwrap()
        .catch(() => undefined);
    setRenaming(null);
  };

  if (isLoading) return <Text fontSize="sm">Loading saved settings…</Text>;

  return (
    <Stack gap={4}>
      <Text fontSize="xs" color="fg.muted">
        Saving a template stores a new revision. Older print jobs keep the
        layout they were printed with. Pick the printer in the print dialog.
      </Text>
      <Stack gap={2}>
        <Text fontWeight="semibold" fontSize="sm">
          Label templates
        </Text>
        {(templates?.items ?? []).map((t) => (
          <HStack
            key={t.id}
            borderWidth="1px"
            borderRadius="md"
            px={3}
            py={2}
            justify="space-between"
            flexWrap="wrap"
            gap={2}
          >
            {renaming?.kind === 'template' && renaming.id === t.id ? (
              <HStack>
                <Input
                  size="sm"
                  value={renaming.name}
                  onChange={(e) =>
                    setRenaming({ ...renaming, name: e.target.value })
                  }
                  aria-label={`Rename ${t.name}`}
                />
                <Button size="xs" onClick={rename}>
                  Save
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </Button>
              </HStack>
            ) : (
              <Box minW={0}>
                <Text fontWeight="semibold" fontSize="sm">
                  {t.name}
                  {t.is_shared && <Badge ml={1}>Shared</Badge>}
                  {t.is_shared_default && <Badge ml={1}>Team default</Badge>}
                  {defaults?.template_id === t.id && (
                    <Badge ml={1}>My default</Badge>
                  )}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {t.spec
                    ? `${t.spec.label.width_mm} × ${t.spec.label.height_mm} mm · ${
                        t.spec.codes.mode === 'both'
                          ? 'QR + barcode'
                          : t.spec.codes.primary
                      } · `
                    : ''}
                  revision {t.revision_number}
                  {t.owner ? ` · ${t.owner}` : ''}
                </Text>
              </Box>
            )}
            <HStack gap={1} flexWrap="wrap">
              <Button
                size="xs"
                variant="ghost"
                aria-label={`Make ${t.name} my default`}
                onClick={() => setDefault({ template_id: t.id })}
              >
                <LuStar /> Make my default
              </Button>
              {t.is_shared && canShared && !t.is_shared_default && (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() =>
                    patchTemplate({ id: t.id, is_shared_default: true })
                  }
                >
                  Team default
                </Button>
              )}
              <Button
                size="xs"
                variant="ghost"
                onClick={() => templateAction({ id: t.id, action: 'duplicate' })}
              >
                <LuCopy /> Duplicate
              </Button>
              {t.can_edit && (
                <Button
                  size="xs"
                  variant="ghost"
                aria-label={`Rename ${t.name}`}
                onClick={() =>
                  setRenaming({ id: t.id, name: t.name, kind: 'template' })
                }
              >
                <LuPencil /> Rename
              </Button>
              )}
              {t.can_edit && (
                <Button
                  size="xs"
                  variant="ghost"
                aria-label={`Archive ${t.name}`}
                onClick={() => templateAction({ id: t.id, action: 'archive' })}
              >
                <LuArchive /> Archive
              </Button>
              )}
            </HStack>
          </HStack>
        ))}
        {!templates?.items.length && (
          <Text fontSize="sm" color="fg.muted">
            No saved templates yet. Use Save settings to create one.
          </Text>
        )}
      </Stack>
      <Stack gap={2}>
        <Text fontWeight="semibold" fontSize="sm">
          Printer profiles
        </Text>
        {(profiles?.items ?? []).map((p) => (
          <HStack
            key={p.id}
            borderWidth="1px"
            borderRadius="md"
            px={3}
            py={2}
            justify="space-between"
            flexWrap="wrap"
            gap={2}
          >
            {renaming?.kind === 'profile' && renaming.id === p.id ? (
              <HStack>
                <Input
                  size="sm"
                  value={renaming.name}
                  onChange={(e) =>
                    setRenaming({ ...renaming, name: e.target.value })
                  }
                  aria-label={`Rename ${p.name}`}
                />
                <Button size="xs" onClick={rename}>
                  Save
                </Button>
                <Button size="xs" variant="ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </Button>
              </HStack>
            ) : (
              <Box minW={0}>
                <Text fontWeight="semibold" fontSize="sm">
                  {p.name}
                  {p.is_shared && <Badge ml={1}>Shared</Badge>}
                  {defaults?.printer_profile_id === p.id && (
                    <Badge ml={1}>My default</Badge>
                  )}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {p.spec.media === 'sheet' ? 'Label sheet' : 'Thermal roll'} ·{' '}
                  {p.spec.dpi ?? 'vector'} dpi · offset {p.spec.offset_x_mm},{' '}
                  {p.spec.offset_y_mm} mm
                </Text>
              </Box>
            )}
            <HStack gap={1} flexWrap="wrap">
              <Button
                size="xs"
                variant="ghost"
                aria-label={`Make ${p.name} my default`}
                onClick={() => setDefault({ printer_profile_id: p.id })}
              >
                <LuStar /> Make my default
              </Button>
              {p.can_edit && (
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label={`Rename ${p.name}`}
                  onClick={() =>
                    setRenaming({ id: p.id, name: p.name, kind: 'profile' })
                  }
                >
                  <LuPencil /> Rename
                </Button>
              )}
              {p.can_edit && (
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label={`Archive ${p.name}`}
                  onClick={() => patchProfile({ id: p.id, archive: true })}
                >
                  <LuArchive /> Archive
                </Button>
              )}
            </HStack>
          </HStack>
        ))}
        {!profiles?.items.length && (
          <Text fontSize="sm" color="fg.muted">
            No saved printer profiles yet. Use Save settings to create one.
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
